import { useEffect } from 'react';
import { useDebouncedDirtySave } from '@cotera/client/app/hooks/use-debounced-dirty-save';
import type { WorkflowModel } from './workflow.model';

/**
 * The slice of {@link WorkflowModel} this hook depends on. Narrowed to a
 * structural type so it can be exercised with a lightweight double in tests.
 */
export type AutosaveWorkflowModel = Pick<
  WorkflowModel,
  | 'isNew'
  | 'isNewWatchable'
  | 'hasUnsavedChangesWatchable'
  | 'graphWatchable'
  | 'nameWatchable'
>;

type UseWorkflowAutosaveParams = {
  model: AutosaveWorkflowModel;
  /** Persist the draft silently (e.g. `saveWorkflowAction.execute({ silent })`). */
  save: () => void;
  /** When false, autosave is disabled (e.g. read-only / locked graph). */
  enabled?: boolean;
};

/**
 * Continual autosave for the workflow editor — the graph analog of
 * {@link useContinualSave}. Where the Tiptap editors call `scheduleSave` from
 * their `onUpdate` on every change, workflow edits flow through the draft state,
 * so we reschedule off the draft's `graphWatchable` / `nameWatchable`.
 *
 * IMPORTANT: we must reschedule on *every* edit, not just when the dirty flag
 * flips. `hasUnsavedChangesWatchable` is a boolean that stays `true` across
 * consecutive edits (and jotai suppresses notifications for an unchanged derived
 * value), so keying the schedule off the dirty flag would debounce off the
 * *first* edit of an episode and never re-arm for later ones. That made the save
 * fire mid-editing (2s after the first change) — which in turn ran
 * `persistDraft` → rehydrate while the user was still editing, and could leave
 * later edits unsaved until an explicit Ctrl+S. Rescheduling off the draft's
 * value watchables makes the save fire ~2s after the user *pauses* instead.
 *
 * Only already-persisted workflows autosave: a new workflow still carries the
 * default name until the user names it via the explicit save modal, so silently
 * persisting it would create an untitled resource. New workflows are covered by
 * the explicit save + navigation-save paths.
 */
export function useWorkflowAutosave({
  model,
  save,
  enabled = true,
}: UseWorkflowAutosaveParams): void {
  const { scheduleSave } = useDebouncedDirtySave({
    save,
    enabled,
    // Re-checked at fire time: the edit may have been reverted or the workflow
    // saved by another path during the debounce window.
    canSave: () =>
      model.isNew === false &&
      model.hasUnsavedChangesWatchable.snapshot() === true,
  });

  useEffect(() => {
    if (enabled !== true) {
      return;
    }

    const maybeScheduleSave = (): void => {
      if (model.isNew !== false) {
        return;
      }
      if (model.hasUnsavedChangesWatchable.snapshot() !== true) {
        return;
      }
      scheduleSave();
    };

    // Cover a workflow that mounts already dirty (or becomes persisted while
    // dirty) — subscriptions only fire on *subsequent* changes.
    maybeScheduleSave();

    // Reschedule on every draft edit. Graph edits (add/move/delete node, edge,
    // node config) change `graphWatchable`; renames change `nameWatchable`;
    // `isNewWatchable` flips when a brand-new workflow becomes persisted.
    const unsubscribers = [
      model.graphWatchable.subscribe(maybeScheduleSave),
      model.nameWatchable.subscribe(maybeScheduleSave),
      model.isNewWatchable.subscribe(maybeScheduleSave),
    ];
    return () => {
      for (const unsubscribe of unsubscribers) {
        unsubscribe();
      }
    };
  }, [enabled, scheduleSave, model]);
}
